import React, { useEffect, useState } from "react";
import { api, STAFF_KEYS, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, ShieldCheck } from "lucide-react";
import { DhunLogo, AivigoFooter } from "./DhunLogo";

const field = "mt-1 h-11 border-white/10 bg-black/30 text-white";
const label = "text-xs uppercase tracking-widest text-slate-400";

function PubPicker({ slug, setSlug }) {
  const [pubs, setPubs] = useState([]);
  useEffect(() => {
    api.get("/auth/staff/pubs").then(({ data }) => setPubs(data)).catch(() => {});
  }, []);
  return (
    <div>
      <Label className={label}>Pub</Label>
      <Select value={slug} onValueChange={setSlug}>
        <SelectTrigger data-testid="staff-pub-select" className={field}>
          <SelectValue placeholder="Choose your pub…" />
        </SelectTrigger>
        <SelectContent className="border-white/10 bg-[#131822] text-white">
          {pubs.map((p) => (
            <SelectItem key={p.id} value={p.slug} data-testid={`staff-pub-option-${p.slug}`}>{p.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function OtpStep({ ticket, hint, onDone, onBack }) {
  const [otp, setOtp] = useState("");
  const [wait, setWait] = useState(30);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const verify = async () => {
    if (otp.length !== 6) return toast.error("Enter the 6-digit code");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/staff/verify", { ticket, code: otp });
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e, "Verify failed"));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/auth/staff/resend", { ticket });
      setWait(30);
      toast.success("New code sent");
    } catch (e) {
      toast.error(errMsg(e, "Could not resend"));
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-300" data-testid="staff-otp-hint">We emailed a 6-digit code to <b>{hint}</b>. It expires in 9 minutes.</p>
      <Input inputMode="numeric" maxLength={6} value={otp} autoFocus
        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
        onKeyDown={(e) => e.key === "Enter" && verify()}
        data-testid="staff-otp" className="h-12 border-white/10 bg-black/30 text-center font-mono text-xl tracking-widest text-white" />
      <Button onClick={verify} disabled={loading} data-testid="staff-verify"
        className="h-12 w-full rounded-full bg-gradient-to-r from-violet-500 to-pink-500 font-bold">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & enter"}
      </Button>
      <div className="flex justify-between text-xs">
        <button onClick={onBack} className="text-slate-400 hover:text-white" data-testid="staff-back">← Start again</button>
        <button onClick={resend} disabled={wait > 0} data-testid="staff-resend"
          className="text-violet-300 hover:text-white disabled:text-slate-600">
          {wait > 0 ? `Resend in ${wait}s` : "Resend code"}
        </button>
      </div>
    </div>
  );
}

/** Staff sign-in: DJ/Admin = pub + password; Owner = email + password; then email OTP. */
export default function StaffLoginGate({ role, onLogin, heading }) {
  const [slug, setSlug] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [otpState, setOtpState] = useState(null);
  const [loading, setLoading] = useState(false);
  const isOwner = role === "owner";

  const submit = async () => {
    if (!isOwner && !slug) return toast.error("Please pick your pub");
    if ((isOwner && !email.trim()) || !password || !code.trim()) return toast.error("Fill in all fields");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/staff/login", { role, password, code: code.trim(), email: isOwner ? email.trim() : null, slug: isOwner ? null : slug });
      setOtpState({ ticket: data.ticket, hint: data.email_hint });
    } catch (e) {
      toast.error(errMsg(e, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const done = (data) => {
    localStorage.setItem(STAFF_KEYS[role], data.token);
    toast.success("Signed in");
    onLogin(data);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0B0E14] px-4 text-white">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#131822] p-8" data-testid={`staff-login-${role}`}>
        <div className="mb-6 flex items-center gap-3">
          <DhunLogo size={44} />
          <div>
            <p className="text-xs uppercase tracking-widest text-violet-300">{heading}</p>
            <p className="text-xl font-extrabold">{otpState ? "Enter your email code" : "Secure sign-in"}</p>
          </div>
        </div>
        {otpState ? (
          <OtpStep {...otpState} onDone={done} onBack={() => setOtpState(null)} />
        ) : (
          <div className="space-y-3">
            {isOwner ? (
              <div>
                <Label className={label}>Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="staff-email" className={field} />
              </div>
            ) : (
              <PubPicker slug={slug} setSlug={setSlug} />
            )}
            <div>
              <Label className={label}>{isOwner ? "Owner code" : "Pub code"}</Label>
              <Input type="password" autoComplete="off" value={code} onChange={(e) => setCode(e.target.value)} data-testid="staff-code" className={field} />
            </div>
            <div>
              <Label className={label}>Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="staff-password" className={field} />
            </div>
            <Button onClick={submit} disabled={loading} data-testid="staff-submit"
              className="h-11 w-full rounded-full bg-gradient-to-r from-violet-500 to-pink-500 font-bold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="mr-2 h-4 w-4" /> Continue</>}
            </Button>
          </div>
        )}
        <AivigoFooter className="mt-6" />
      </div>
    </div>
  );
}
